// Extraction mécanique de client.ts (WO-V3-02 / US-019) — types du domaine.
/** MJ-07 : un job lancé depuis une approbation (`GET /approvals/{id}/jobs`). */
export interface ApprovalLaunchedJob {
  job_id: string
  /** Type du job (`scaffold_app`, `draft_specification`…). */
  type: string | null
  status: 'pending' | 'processing' | 'done' | 'failed' | 'worker_lost'
  /** Cause de l'échec, seulement si `status = failed`. */
  error: string | null
  created_at: string
  updated_at: string
}

export interface JobSummary {
  job_id: string
  /**
   * `worker_lost` est un état DÉRIVÉ côté passerelle (WO-V5-B, `JobEventsService`) : un job
   * `processing` dont le bail ou le heartbeat du worker a expiré, JAMAIS écrit en base — la
   * colonne SQL reste `processing`. Sans cette valeur, un worker mort produisait un spinner
   * infini (F P1 D6) : l'écran n'avait aucun état à afficher.
   */
  status: 'pending' | 'processing' | 'done' | 'failed' | 'worker_lost'
  document_id: string | null
  filename: string | null
  error: string | null
  created_at: string
  updated_at: string
  /** Présent uniquement si le job a été pris (`processing`/`worker_lost`). */
  lease_expires_at?: string | null
  /** Dernier battement connu du worker propriétaire, s'il existe. */
  worker_last_seen_at?: string | null
}

export interface JobEvent {
  seq: number
  event: string
  /** JSON sérialisé — la passerelle transmet la colonne telle quelle. */
  details: string
  schema_version: number
  created_at: string
}

export interface SemanticHistoryHit {
  job_id: string
  seq: number
  event: string
  details: string
  score: number
  created_at: string
  trace_id?: string
}

export interface PortfolioRow {
  workspace_id: string
  name: string
  documents: number
  jobs_pending: number
  jobs_processing: number
  approvals_pending: number
  oldest_pending_approval_s: number | null
  /** État du projet (`active` | `archived`). Absent d'une passerelle antérieure. */
  status?: 'active' | 'archived' | string
  /**
   * L'appelant est-il membre du projet ? Toujours vrai hors administrateur de la plateforme, qui
   * voit TOUS les projets (ARB-SEC-01) et rejoint ceux où il ne l'est pas. Absent = membre.
   */
  membre?: boolean
}

export interface RecentDecision {
  id: number
  workspace_id: string
  artifact_path: string
  requested_by: string
  status: 'approved' | 'rejected'
  decided_by: string
  /**
   * AUD-CRAWL-K2 : nom affiché du décideur (« Prénom Nom », sinon son identifiant de connexion), par
   * jointure `users` du locataire ; `null` s'il n'est pas un utilisateur. Absent d'une passerelle antérieure.
   */
  decided_by_name?: string | null
  decision_comment: string | null
  decided_at: string
}

/**
 * v05x-ux-vague1 (L2, UXA-DC02) — « qui doit agir, à quelle étape, et pourquoi pas moi »,
 * calculé par la PASSERELLE (`ApprovalNextStep.java`) avec les mêmes règles que les refus.
 * L'interface n'invente aucune règle : elle affiche ce champ. Absent d'une passerelle antérieure.
 */
export type CodeEtape =
  | 'points' | 'review' | 'quality_check' | 'decision' | 'revision'
  | 'approved' | 'rejected' | 'superseded' | 'expired'

export interface EtapeRequise {
  code: 'points' | 'review' | 'quality_check' | 'decision' | 'revision'
  /** v05x-ux-vague2 : nombre de points qui bloquent (étape `points`). */
  count?: number
  /** `done` faite · `current` celle qu'on annonce · `running` en cours (contrôle) · `blocked` attend une étape antérieure · `todo` plus tard */
  state: 'done' | 'current' | 'running' | 'blocked' | 'todo' | 'pending'
  eligible_user_ids: string[]
  by?: string | null
  verdict?: string | null
  status?: string | null
  requires_mitigation?: boolean
  two_person_mode?: boolean
}

export interface NextStep {
  code: CodeEtape
  /** `mine` : à vous d'agir · `others` : en attente d'une autre personne · `done` : terminée */
  section: 'mine' | 'others' | 'done'
  actor_role: 'reviewer' | 'approver' | 'member' | 'system' | null
  eligible_user_ids: string[]
  can_act: boolean
  /** Le refus que l'appelant recevrait s'il tentait l'étape (mêmes codes que les services). */
  refusal_code: string | null
  reason: string | null
  in_progress?: boolean
  requires_mitigation?: boolean
  two_person_mode?: boolean
  /** Verdict « reject » : l'APPROBATION de cette version est refusée avec ce code. */
  approval_refusal_code?: string | null
  /** v05x-ux-vague2 (étape `points`) : nombre de points qui bloquent l'approbation. */
  points_count?: number
  steps: EtapeRequise[]
}

export interface ApprovalSummary {
  id: number
  job_id: string
  artifact_path: string
  requested_by: string
  status: 'pending' | 'approved' | 'rejected'
  decided_by: string | null
  decision_comment: string | null
  model: string | null
  prompt_card: string | null
  prompt_card_version: string | null
  /** Lignée de co-écriture (EPIC-COÉCRITURE) : rang de version + supersession. */
  version_n: number | null
  lineage_id: string | null
  superseded_by_job_id: string | null
  /** M5 : version COURANTE de la lignée quand celle-ci est remplacée (`null` : la remplaçante est en production). */
  current_approval_id?: number | null
  current_version_n?: number | null
  instructions: string | null
  /** Dernière évaluation machine et preuve épinglée humaine : autorités distinctes. */
  latest_evaluation?: SpecGateAuthority | null
  approved_evidence?: SpecGateAuthority | null
  spec_gate_status?: string | null
  spec_gate_receipt_id?: string | null
  approved_spec_gate_receipt_id?: string | null
  spec_gate_passed?: boolean | null
  spec_gate_score?: number | null
  spec_gate_checks_passed?: number | null
  spec_gate_failed_checks?: string | null
  spec_gate_reasons?: string | null
  spec_gate_evaluation_job_id?: string | null
  /** FND-V050-081 : publication d'un artefact APPROUVÉ (nul hors approuvé). `failing` : le
   *  worker a déjà échoué à publier et réessaie ; `blocked` : blocage terminal (V144). */
  published_at?: string | null
  publication_blocked_at?: string | null
  /** Motif du blocage terminal (V144), ex. `PUBLICATION_PLAN_MISMATCH…` (plan_divergence). */
  publication_blocked_reason?: string | null
  publication_state?: 'published' | 'pending' | 'failing' | 'blocked' | null
  publication_error?: string | null
  created_at: string
  decided_at: string | null
  /** v05x-ux-vague1 (L2) : titre lisible (premier titre utile du document) ; nul = repli sur le chemin. */
  title?: string | null
  /** Demande d'origine (DEM-n) retrouvée par la filiation des jobs. */
  demand_id?: number | null
  demand_reference?: string | null
  /** Faux pour une version remplacée. */
  is_lineage_head?: boolean
  /** Points à trancher (vague 2, L4) : nuls tant qu'ils ne sont pas calculés — jamais « 0 » inventé. */
  open_points?: number | null
  blocking_points?: number | null
  next_step?: NextStep | null
  review_verdict?: ReviewVerdict | string | null
  reviewed_by?: string | null
}

/** S14 : une consigne humaine déjà donnée sur une version précédente du document. */
export interface PreviousInstruction {
  approval_id: number
  version_n: number
  author_id: string | null
  /** Nom affiché résolu par la passerelle (repli : identifiant). */
  author: string | null
  instructions: string
}

/** S14 : ce qui partira avec la prochaine version, et ce que la borne écarte (nommé). */
export interface PreviousInstructionsPreview {
  items: PreviousInstruction[]
  omitted_versions: number[]
  characters: number
  budget_characters: number
  max_versions: number
}

/** Les trois valeurs admises par `chk_approval_review_verdict_valeurs` (V186). */
export type ReviewVerdict = 'approve' | 'risk' | 'reject'

export interface ApprovalDetail extends ApprovalSummary {
  workspace_id: string
  /** Le contenu INTÉGRAL à publier — copié en base au dépôt de la demande. */
  artifact_content: string
  /** Manifeste JSON sérialisé : carte@version, modèle, empreintes. */
  manifest: string
  /** Prompt/source text that led to this artefact, projected from its immutable manifest. */
  source_prompt?: string | null
  /** RJ-52 : `human_edit` pour une édition à la main (aucun prompt, auteur = éditeur). */
  provenance?: string | null
  /** Auteur d'une édition humaine ; nul pour une génération. */
  human_author?: string | null
  /** Chemin de l'artefact repris par une édition humaine. */
  edited_from?: string | null
  /** Contenu de la version directement remplacée, borné au même tenant/espace. */
  previous_artifact_content?: string | null
  previous_version_n?: number | null
  supersedes_job_id: string | null
  /**
   * S14 (2026-10-07) : consignes humaines déjà données sur ce document (cette version et ses
   * prédécesseurs) que la prochaine version recevra avec la consigne courante — la MÊME sélection
   * bornée que la régénération. Absent d'une passerelle antérieure.
   */
  previous_instructions?: PreviousInstructionsPreview | null
  spec_gate_breakdown?: string | null
  spec_gate_critical_findings?: string | null
  spec_gate_high_findings?: string | null
  spec_gate_evaluated_at?: string | null
  spec_gate_fresh_until?: string | null
  /**
   * Revue indépendante (V186 / v030-ai-012) — projetée par `ApprovalService.detail`.
   * `review_evidence_sha256` n'est VOLONTAIREMENT pas exposée : elle est calculée par le
   * serveur à partir de la justification, le client ne l'envoie ni ne l'affiche.
   */
  review_verdict?: ReviewVerdict | string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  /** Plan de mitigation d'un verdict « risk » (V206) : tout-ou-rien côté base. */
  risk_owner?: string | null
  risk_mitigation?: string | null
  risk_mitigation_due_at?: string | null
  /**
   * O11 : lignée IA (`doit_lignee_generee_par_ia`) — une revue indépendante est requise avant
   * l'approbation. Absent d'une passerelle antérieure.
   */
  review_required?: boolean | null
}

export interface SpecGateEvaluation {
  evaluation_job_id: string
  status: 'pending' | 'processing' | 'model_pass_grounded'
  enqueued: boolean
  receipt_id: string | null
  score: number | null
  specification_version: string
  specification_sha256: string
  source_bundle_sha256?: string | null
  model_pass_grounded?: boolean
  human_verified?: boolean
}

export interface SpecGateAuthority {
  contract_version?: string | null
  status?: string | null
  receipt_id?: string | null
  source_bundle_sha256?: string | null
  model_pass_grounded?: boolean | null
  human_verified?: boolean | null
  can_human_verify?: boolean | null
  /** FND-V05X-004 : la dernière évaluation (PASS courant, autre reçu) peut être rattachée par l'appelant. */
  can_renew_evidence?: boolean | null
  /** FND-V05X-004 : échéance de la preuve épinglée, et qui l'a renouvelée après la décision. */
  fresh_until?: string | null
  renewed_by?: string | null
  renewed_at?: string | null
  human_verified_by?: string | null
  human_verified_at?: string | null
  attestation_version?: string | null
  resolved_count?: number | null
}

export interface SpecGateDecisionEvidence {
  receiptId: string
  sourceBundleSha256: string
  attestationAccepted: boolean
}

export interface SpecGateEvidenceDetail {
  contract_version: string
  receipt_id: string
  source_bundle_sha256: string
  references: Array<{ subject_kind: string; subject_id: string; source_id: string;
    start_line: number; end_line: number; excerpt: string }>
}

export function approvalDecisionBody(
  decision: 'approved' | 'rejected',
  comment?: string,
  evidence?: SpecGateDecisionEvidence | null,
): Record<string, unknown> {
  const body: Record<string, unknown> = { decision }
  if (comment?.trim()) body.comment = comment.trim()
  if (decision === 'approved' && evidence?.receiptId.trim()
      && evidence.sourceBundleSha256.trim() && evidence.attestationAccepted) {
    body.spec_gate_receipt_id = evidence.receiptId.trim()
    body.source_bundle_sha256 = evidence.sourceBundleSha256.trim()
    body.human_attestation = {
      accepted: true,
      contract_version: 'spec-gate-human-attestation/v1',
    }
  }
  return body
}


/** FND-V05X-004 : rattachement d'un reçu renouvelé à une spécification approuvée. */
export interface SpecGateRenewalResult {
  id: number
  renewal_id: number
  previous_receipt_id: string
  spec_gate_receipt_id: string
  source_bundle_sha256: string
  spec_gate_attestation_version: string
  renewed_by: string
  renewed_at: string
  fresh_until: string
  decided_by: string
  repinned_backlog_items: number
}

export function specGateRenewalBody(
  receiptId: string, sourceBundleSha256: string, comment?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    spec_gate_receipt_id: receiptId.trim(),
    source_bundle_sha256: sourceBundleSha256.trim(),
    human_attestation: { accepted: true, contract_version: 'spec-gate-human-attestation/v1' },
  }
  if (comment?.trim()) body.comment = comment.trim()
  return body
}
